import { useEditor, EditorContent } from "@tiptap/react";
import { useState, useEffect } from "react";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  HighlightOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  FontColorsOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
} from "@ant-design/icons";
import type { Editor } from "@tiptap/react";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import CharacterCount from "@tiptap/extension-character-count";
import Placeholder from "@tiptap/extension-placeholder";
import { Button, Select, Dropdown, Divider } from "antd";

import "./TiptapEditor.scss";

type TiptapEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
};

const TiptapEditor = ({ value = "", onChange }: TiptapEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight,
      TextStyle,
      Color,
      CharacterCount.configure({
        limit: 10000, // 可選，設定上限（可不設）
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"], // 想要哪些節點支援對齊
      }),
      Placeholder.configure({
        placeholder: "내용을 입력해주세요.",
      }),
    ],
    content: value,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      if (html !== value) {
        onChange?.(html);
      }
    },
  });

  if (!editor) return null;

  return (
    <div className="tiptap-wrapper">
      {/* 自定 toolbar：傳 editor 實例下去 */}
      <div className="tiptap-toolbar">
        <CustomToolbar editor={editor} />
      </div>

      {/* 編輯區 */}
      <div className="tiptap-editor">
        <EditorContent editor={editor} />
        <p style={{ fontSize: 12, color: "#000000", opacity: 0.45 }}>
          {editor?.storage.characterCount.characters()}/10000
        </p>
      </div>
    </div>
  );
};

export default TiptapEditor;

const CustomToolbar = ({ editor }: { editor: Editor }) => {
  const [blockType, setBlockType] = useState("paragraph");

  const colorOptions = [
    "#000000", // 黑
    "#f5222d", // 紅
    "#fa8c16", // 橘
    "#52c41a", // 綠
    "#1677ff", // 藍
    "#fadb14", // 黃
    "#722ed1", // 紫
  ];

  const menuItems = [
    {
      key: "colors",
      label: (
        <div style={{ display: "flex", gap: 8, padding: 4 }}>
          {colorOptions.map((color) => (
            <div
              key={color}
              onClick={() => {
                editor.chain().focus().setColor(color).run();
              }}
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                backgroundColor: color,
                border: "1px solid #ccc",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!editor) return;

    const updateBlockType = () => {
      if (editor.isActive("paragraph")) return setBlockType("paragraph");
      for (let i = 1; i <= 5; i++) {
        if (editor.isActive("heading", { level: i }))
          return setBlockType(`H${i}`);
      }
      setBlockType("paragraph"); // fallback
    };

    // 初始設定一次
    updateBlockType();

    // 註冊 selection 更新事件
    editor.on("selectionUpdate", updateBlockType);
    editor.on("update", updateBlockType);

    // 清理時移除監聽器
    return () => {
      editor.off("selectionUpdate", updateBlockType);
    };
  }, [editor]);

  const handleChange = (value: string) => {
    if (!editor) return;

    if (value === "paragraph") {
      editor.chain().focus().setParagraph().run();
    } else if (value.startsWith("H")) {
      const level = parseInt(value[1]);
      editor
        .chain()
        .focus()
        .toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 })
        .run();
    }
  };

  if (!editor) return null;

  return (
    <div className="w-full toolbar">
      <Button
        type="text"
        icon={
          <img
            alt="undo"
            src={require("../../../../assets/icons/undo.png")}
            className="icon"
          />
        }
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      />
      <Button
        type="text"
        icon={
          <img
            alt="redo"
            src={require("../../../../assets/icons/redo.png")}
            className="icon"
          />
        }
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      />

      <Select
        style={{ width: "25%" }}
        value={blockType}
        options={[
          { label: "본문", value: "paragraph" },
          { label: "H1", value: "H1" },
          { label: "H2", value: "H2" },
          { label: "H3", value: "H3" },
          { label: "H4", value: "H4" },
          { label: "H5", value: "H5" },
        ]}
        onChange={handleChange}
      />
      <Divider type="vertical" className="horizontal-divider" />
      <Dropdown
        menu={{ items: menuItems }}
        trigger={["click"]}
        placement="bottomLeft"
      >
        <Button
          type="text"
          icon={<FontColorsOutlined />}
          onClick={(e) => e.preventDefault()} // 防止 Dropdown auto close
        />
      </Dropdown>
      <Button
        type={editor.isActive("highlight") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        icon={<HighlightOutlined />}
      />
      <Button
        type={editor.isActive("bold") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={<BoldOutlined />}
      />
      <Button
        type={editor.isActive("italic") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        icon={<ItalicOutlined />}
      />
      <Button
        type={editor.isActive("underline") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        icon={<UnderlineOutlined />}
      />
      <Button
        type={editor.isActive("strike") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        icon={<StrikethroughOutlined />}
      />
      <Divider type="vertical" className="horizontal-divider" />
      <Button
        type={editor.isActive("orderedList") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        icon={<OrderedListOutlined />}
      />
      <Button
        type={editor.isActive("bulletList") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        icon={<UnorderedListOutlined />}
      />
      <Divider type="vertical" className="horizontal-divider" />
      <Button
        type={editor.isActive({ textAlign: "left" }) ? "primary" : "text"}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        icon={<AlignLeftOutlined />}
      />
      <Button
        type={editor.isActive({ textAlign: "center" }) ? "primary" : "text"}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        icon={<AlignCenterOutlined />}
      />
      <Button
        type={editor.isActive({ textAlign: "right" }) ? "primary" : "text"}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        icon={<AlignRightOutlined />}
      />
      <Divider type="vertical" className="horizontal-divider" />
      <Button
        type="text"
        icon={
          <img
            alt="code"
            src={require("../../../../assets/icons/code.png")}
            className="icon"
          />
        }
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />

      <Button
        type="text"
        icon={
          <img
            alt="quote"
            src={require("../../../../assets/icons/quote.png")}
            className="icon"
          />
        }
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />

      <Button
        type="text"
        icon={
          <img
            alt="horizontalrule"
            src={require("../../../../assets/icons/horizontalrule.png")}
            className="icon"
          />
        }
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />
    </div>
  );
};
